/**
 * The Metric_Registry and its boot-time consistency assertion
 * (`complan-governance/requirements.md` Requirement 2).
 *
 * A declarative registry, not definitions scattered across screens: every
 * Governance_Metric is a value in {@link GOVERNANCE_METRICS}, and nothing else in
 * the system decides what a displayed figure means. R2.1 makes this the only
 * source of a metric's label, numerator, denominator, unit and exclusions, and
 * R2.9 makes it the same source on every screen - which holds because this module
 * is in `packages/domain` and both the API and the web client import it.
 *
 * The shape is Foundation's `validation-registry.ts`: a mapping over a closed
 * identifier union, exhaustive by construction, plus an assertion for the rules
 * the type system cannot state.
 *
 * ## What the type system already guarantees
 *
 * {@link GOVERNANCE_METRICS} is `Readonly<{ [Id in MetricId]: MetricDefinition<Id> }>`.
 * A missing entry is an incomplete mapping and an extra entry has no key, so both
 * fail to compile; and because each value is `MetricDefinition<Id>` for that
 * key's own `Id`, an entry filed under the wrong key does not type either.
 *
 * ## What {@link assertMetricRegistryConsistent} adds
 *
 * Four rules the types cannot express, all of them R2.3's:
 *
 * 1. Non-empty label and numerator keys - a definition with an empty label
 *    renders as a blank tooltip rather than failing.
 * 2. `denominatorKey` non-null exactly when `viewerScoped` - the biconditional,
 *    both directions. A Viewer_Scoped metric with no described denominator is
 *    unexplainable, and a count carrying one is describing something it does not
 *    compute.
 * 3. `unit === 'percent'` for every Viewer_Scoped entry - R1.4 marks shares,
 *    ratios and percentages Viewer_Scoped and nothing else, so a Viewer_Scoped
 *    count would mean one of the two flags is wrong.
 * 4. Distinct label keys - two metrics sharing a label are indistinguishable on
 *    screen, which is a reporting defect rather than a cosmetic one.
 *
 * It is called from `boot-assertions.ts` beside Foundation's own registry
 * assertion, so a drifted registry fails at start-up rather than at a reader's
 * tooltip.
 */
import { GOVERNANCE_METRIC_IDS, METRIC_EXCLUSION_KEYS as EX, } from './metric.js';
/** Shorthand for the exclusion set every Initiative-population metric carries. */
const POPULATION_EXCLUSIONS = Object.freeze([EX.cancelled, EX.unreadable]);
/** Shorthand for a metric whose value comes from the Manpower engine (Assumption A15). */
const MANPOWER_EXCLUSIONS = Object.freeze([EX.unreadable, EX.dataIncomplete]);
/**
 * A count of Initiative records over the Readable_Set. Not Viewer_Scoped (R1.4):
 * an absolute count is already understood to cover what the reader can see, and
 * Foundation's counts have carried that meaning since MVP 1 (its Assumption A4).
 */
function countOf(id, criterion, exclusionKeys = POPULATION_EXCLUSIONS) {
    return {
        id,
        labelKey: `governance.metric.${id}.label`,
        numeratorKey: `governance.metric.${id}.numerator`,
        denominatorKey: null,
        unit: 'count',
        exclusionKeys,
        viewerScoped: false,
        criterion,
    };
}
/** An hours total from the Manpower engine. Not Viewer_Scoped, for the same reason a count is not. */
function hoursOf(id, criterion) {
    return {
        id,
        labelKey: `governance.metric.${id}.label`,
        numeratorKey: `governance.metric.${id}.numerator`,
        denominatorKey: null,
        unit: 'hours',
        exclusionKeys: MANPOWER_EXCLUSIONS,
        viewerScoped: false,
        criterion,
    };
}
/**
 * A share, ratio or percentage. Always Viewer_Scoped (R1.4) and always carrying a
 * described denominator (R2.3), because this constructor is the only way to
 * declare one and it sets both.
 */
function shareOf(id, criterion, exclusionKeys = POPULATION_EXCLUSIONS) {
    return {
        id,
        labelKey: `governance.metric.${id}.label`,
        numeratorKey: `governance.metric.${id}.numerator`,
        denominatorKey: `governance.metric.${id}.denominator`,
        unit: 'percent',
        exclusionKeys,
        viewerScoped: true,
        criterion,
    };
}
/**
 * The complete Metric_Registry (R2.1).
 *
 * Every entry cites the requirement criterion it implements, because these
 * entries are the machine-readable form of Requirements 3 and 4 and an entry
 * without a citation is a metric nobody asked for.
 */
export const GOVERNANCE_METRICS = Object.freeze({
    // --- counts ---
    initiative_count: countOf('initiative_count', 'R4.2'),
    readiness_complete_initiative_count: countOf('readiness_complete_initiative_count', 'R3.3', [
        ...POPULATION_EXCLUSIONS,
        EX.noRequiredMilestone,
    ]),
    readiness_overdue_initiative_count: countOf('readiness_overdue_initiative_count', 'R3.3'),
    readiness_due_initiative_count: countOf('readiness_due_initiative_count', 'R3.3'),
    readiness_missing_initiative_count: countOf('readiness_missing_initiative_count', 'R3.3'),
    readiness_not_applicable_initiative_count: countOf('readiness_not_applicable_initiative_count', 'R8.8', [...POPULATION_EXCLUSIONS, EX.noRequiredMilestone]),
    changed_after_approval_initiative_count: countOf('changed_after_approval_initiative_count', 'R3.4'),
    unresolved_watchout_initiative_count: countOf('unresolved_watchout_initiative_count', 'R3.4'),
    unresolved_watchout_count: countOf('unresolved_watchout_count', 'R3.4'),
    high_severity_unresolved_watchout_initiative_count: countOf('high_severity_unresolved_watchout_initiative_count', 'R4.4'),
    dte_involvement_initiative_count: countOf('dte_involvement_initiative_count', 'R3.5'),
    csc_involvement_initiative_count: countOf('csc_involvement_initiative_count', 'R3.5'),
    open_dependency_initiative_count: countOf('open_dependency_initiative_count', 'R3.5'),
    overdue_dependency_count: countOf('overdue_dependency_count', 'R3.5'),
    governance_exception_initiative_count: countOf('governance_exception_initiative_count', 'R3.7'),
    warning_exception_initiative_count: countOf('warning_exception_initiative_count', 'R3.7'),
    ownership_gap_initiative_count: countOf('ownership_gap_initiative_count', 'R3.7'),
    // A count of *periods*, not of Initiatives, so it carries the Manpower
    // exclusions rather than the population ones.
    over_capacity_period_count: {
        id: 'over_capacity_period_count',
        labelKey: 'governance.metric.over_capacity_period_count.label',
        numeratorKey: 'governance.metric.over_capacity_period_count.numerator',
        denominatorKey: null,
        unit: 'count',
        exclusionKeys: MANPOWER_EXCLUSIONS,
        viewerScoped: false,
        criterion: 'R3.6',
    },
    // --- hours ---
    excess_demand_hours: hoursOf('excess_demand_hours', 'R3.6'),
    demand_hours_total: hoursOf('demand_hours_total', 'R4.2'),
    // --- shares: the Viewer_Scoped set ---
    priority_share: shareOf('priority_share', 'R3.2'),
    finality_share: shareOf('finality_share', 'R3.2'),
    milestone_readiness_share: shareOf('milestone_readiness_share', 'R8.7', [
        ...POPULATION_EXCLUSIONS,
        EX.noRequiredMilestone,
    ]),
    ff_utilization: shareOf('ff_utilization', 'R4.2', MANPOWER_EXCLUSIONS),
});
/** Raised by {@link assertMetricRegistryConsistent}; the message names the offending metric. */
export class MetricRegistryInconsistentError extends Error {
    constructor(message) {
        super(message);
        this.name = 'MetricRegistryInconsistentError';
    }
}
/**
 * Asserts the four R2.3 rules the type system cannot state. Called at boot, before
 * a socket is bound.
 *
 * Throws on the first violation rather than collecting them: unlike the
 * Validation_Engine, whose findings are shown to a user and must be exhaustive
 * (Foundation R8.12), this is a programming error discovered at start-up, and the
 * first one names the fix.
 */
export function assertMetricRegistryConsistent() {
    const seenLabelKeys = new Set();
    for (const id of GOVERNANCE_METRIC_IDS) {
        const definition = GOVERNANCE_METRICS[id];
        if (definition.id !== id) {
            throw new MetricRegistryInconsistentError(`Metric ${id} is filed under a key that does not match its own id (${definition.id}).`);
        }
        if (definition.labelKey.trim() === '') {
            throw new MetricRegistryInconsistentError(`Metric ${id} holds an empty label key.`);
        }
        if (definition.numeratorKey.trim() === '') {
            throw new MetricRegistryInconsistentError(`Metric ${id} holds an empty numerator key.`);
        }
        // R2.3, both directions of the biconditional.
        if (definition.viewerScoped && definition.denominatorKey === null) {
            throw new MetricRegistryInconsistentError(`Metric ${id} is Viewer_Scoped but describes no denominator (R2.3).`);
        }
        if (!definition.viewerScoped && definition.denominatorKey !== null) {
            throw new MetricRegistryInconsistentError(`Metric ${id} is not Viewer_Scoped but describes a denominator it does not compute (R2.3).`);
        }
        // R1.4: Viewer_Scoped marks shares, ratios and percentages and nothing else.
        if (definition.viewerScoped && definition.unit !== 'percent') {
            throw new MetricRegistryInconsistentError(`Metric ${id} is Viewer_Scoped but its unit is ${definition.unit}; only a percent can be (R1.4).`);
        }
        if (seenLabelKeys.has(definition.labelKey)) {
            throw new MetricRegistryInconsistentError(`Metric ${id} reuses the label key ${definition.labelKey}; two metrics sharing a label are indistinguishable on screen.`);
        }
        seenLabelKeys.add(definition.labelKey);
    }
}
/**
 * The Metric_Definition of `id` (R2.1, R2.9).
 *
 * A total function over {@link MetricId} - there is no missing case and therefore
 * no `undefined` to handle at a call site.
 */
export function metricDefinition(id) {
    return GOVERNANCE_METRICS[id];
}
/** Every Viewer_Scoped Metric_Id, for the R1.5 notice and the Property 54 generator. */
export function viewerScopedMetricIds() {
    return GOVERNANCE_METRIC_IDS.filter((id) => GOVERNANCE_METRICS[id].viewerScoped);
}
//# sourceMappingURL=metric-registry.js.map